import React, { useState, useEffect } from "react";
import axios from "axios";
import { useUserData } from "../context/UserDataContext";

type DeviceDataItem = {
  key: string;
  name: string;
  value: string;
  unit: string;
};

type DeviceData = {
  deviceSn: string;
  deviceType: string;
  deviceState: string;
  requestId: string;
  collectionTime: number;
  dataList: DeviceDataItem[];
};

type HistoricalItem = {
  collectTime: string;
  dataList: DeviceDataItem[];
};

type HistoricalData = {
  success: boolean;
  paramDataList: HistoricalItem[];
};

const SolarSofar: React.FC = () => {
  const [access_token, setAccessToken] = useState<string | undefined>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [data, setData] = useState<DeviceData | null>(null);
  const [historicalData, setHistoricalData] = useState<HistoricalData | null>(null);
  const [startDate, setStartDate] = useState("2024-01-01");
  const [endDate, setEndDate] = useState("2024-01-31");
  const [timeType, setTimeType] = useState<number>(3); // Miesiąc

  const { userData } = useUserData();

  const solarToken = userData.solarmanToken;
  const solarmanEmail = userData.solarmanEmail;
  const solarmanPassword = userData.solarmanPassword;

  useEffect(() => {
    loginSolarSofarAPI();
  }, []);

  const loginSolarSofarAPI = () => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/solarsofar/login`)
      .then((response) => {
        const token = response.data.access_token;
        setAccessToken(token);
        callForData(token);
      })
      .catch((error) => {
        console.error("❌ Błąd logowania:", error);
        setErrorMessage("Błąd logowania do API");
      });
  };

  const callForData = (accessToken: string) => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/solarsofar/currentData?access_token=${accessToken}`)
      .then((response) => {
        setData(response.data);
        if (response.data.success === false) {
          setErrorMessage(response.data.msg);
        }
      })
      .catch((error) => {
        console.error("❌ Błąd przy pobieraniu danych:", error);
      });
  };

  const callForHistorical = (accessToken: string) => {
    const deviceSn = data?.deviceSn;
    if (!deviceSn) {
      setErrorMessage("Brak numeru SN urządzenia.");
      return;
    }

    axios
      .get(`${process.env.REACT_APP_API_URL}api/solarsofar/historical`, {
        params: {
          access_token: accessToken,
          deviceSn,
          startTime: startDate,
          endTime: endDate,
          timeType,
        },
      })
      .then((response) => {
        if (response.data.success === false) {
          setErrorMessage(response.data.msg);
        } else {
          setHistoricalData(response.data);
        }
      })
      .catch((error) => {
        console.error("❌ Błąd przy pobieraniu danych historycznych:", error);
      });
  };

  const callDeviceList = (accessToken: string) => {
    axios
      .get(`${process.env.REACT_APP_API_URL}api/solarsofar/deviceList?access_token=${accessToken}`)
      .then((response) => {
        console.log("📟 Lista urządzeń:", response.data.deviceList);
      })
      .catch((error) => {
        console.error("❌ Błąd pobierania deviceList:", error);
      });
  };

  // const callForAlerts = (accessToken: string) => {
  //   const now = Math.floor(Date.now() / 1000);
  //   const sevenDaysAgo = now - 7 * 24 * 60 * 60;

  //   axios
  //     .get(`${process.env.REACT_APP_API_URL}api/solarsofar/alertList`, {
  //       params: {
  //         access_token: accessToken,
  //         deviceSn,
  //         deviceId,
  //         startTimestamp: sevenDaysAgo,
  //         endTimestamp: now,
  //       },
  //     })
  //     .then((res) => {
  //       console.log("🚨 Alerty urządzenia:", res.data.alertList);
  //     })
  //     .catch((err) => {
  //       console.error("❌ Błąd przy pobieraniu alertów:", err);
  //     });
  // };

  return (
    <div className="Se">
      <header className="Se-header">Monitoring Solar Sofar Solarman</header>
      <h4>{access_token ? "✅ Połączono z API" : "❌ Brak połączenia z API"}</h4>
      <h4>{errorMessage}</h4>

      <button onClick={() => callDeviceList(access_token!)}>📟 Pobierz listę urządzeń</button>

      {data && (
        <>
          <h3>Dane historyczne</h3>
          <div style={{ marginBottom: "10px" }}>
            <label>
              Początek:
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label style={{ marginLeft: "10px" }}>
              Koniec:
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
            <label style={{ marginLeft: "10px" }}>
              Typ czasu:
              <select value={timeType} onChange={(e) => setTimeType(parseInt(e.target.value))}>
                <option value={1}>Frame</option>
                <option value={2}>Dzień</option>
                <option value={3}>Miesiąc</option>
                <option value={4}>Rok</option>
              </select>
            </label>
            <button style={{ marginLeft: "10px" }} onClick={() => callForHistorical(access_token!)}>
              Pobierz dane historyczne
            </button>
            {/* <button style={{ marginLeft: "10px" }} onClick={() => callForAlerts(access_token!)}>
              Pobierz dane historyczne
            </button> */}
          </div>

          {historicalData?.paramDataList?.map((param, index) => (
            <div key={index} style={{ marginBottom: "10px" }}>
              <strong>Czas: {param.collectTime}</strong>
              <ul>
                {param.dataList.map((item, idx) => (
                  <li key={idx}>
                    <strong>{item.name}</strong>: {item.value} {item.unit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}

      {data && (
        <>
          <h1>Dane urządzenia</h1>
          <p>SN: {data.deviceSn}</p>
          <p>Typ: {data.deviceType}</p>
          <p>Status: {data.deviceState}</p>
          <p>Request ID: {data.requestId}</p>
          <p>Odczyt z: {new Date(data.collectionTime * 1000).toLocaleString()}</p>
          <h2>Lista parametrów</h2>
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Nazwa</th>
                <th>Wartość</th>
                <th>Jednostka</th>
              </tr>
            </thead>
            <tbody>
              {data.dataList.map((item, index) => (
                <tr key={index}>
                  <td>{item.key}</td>
                  <td>{item.name}</td>
                  <td>{item.value}</td>
                  <td>{item.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default SolarSofar;
